import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './style1.css';

interface AddDemoProps {
    username: string | null;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

const AddDemo: React.FC<AddDemoProps> = ({ username, onLogout }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [videoSuffix, setVideoSuffix] = useState('');
    const [imageSuffix, setImageSuffix] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Only admin can access
    if (username !== 'admin') {
        return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Access denied.</div>;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!name.trim() || !description.trim()) {
            setError('Name and description are required.');
            return;
        }

        // Only add relative path if user entered something
        const videoUrl = videoSuffix.trim()
            ? `/demos/video/${videoSuffix.trim()}`
            : undefined;
        const imageUrl = imageSuffix.trim()
            ? `/demos/images/${imageSuffix.trim()}`
            : undefined;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/adddemo', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    name,
                    description,
                    videoUrl,
                    imageUrl
                })
            });
            if (response.ok) {
                setSuccess('Demo added!');
                setName('');
                setDescription('');
                setVideoSuffix('');
                setImageSuffix('');
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to add demo.');
            }
        } catch {
            setError('Network error.');
        }
    };

    return (
        <div className="modern-layout">
            <header className="modern-header">
                <div className="header-container">
                    <nav className="modern-nav">
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        {onLogout && (
                            <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                        )}
                    </nav>
                </div>
            </header>
            <main className="modern-content">
                <div
                    style={{
                        maxWidth: 500,
                        margin: '2rem auto',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 2px 16px #6a11cb22',
                        padding: '2rem'
                    }}
                >
                    <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Add Demo</h2>
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Name<span style={{ color: 'red' }}>*</span>:</label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem', marginTop: 4 }}
                                required
                            />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Description<span style={{ color: 'red' }}>*</span>:</label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: 120,
                                    padding: '0.5rem',
                                    marginTop: 4,
                                    fontFamily: 'inherit',
                                    fontSize: '1rem'
                                }}
                                required
                            />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Video (suffix only):</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#888', fontSize: '0.95em' }}>
                                    /demos/video/
                                </span>
                                <input
                                    type="text"
                                    value={videoSuffix}
                                    onChange={e => setVideoSuffix(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', marginLeft: 4 }}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Image (suffix only):</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#888', fontSize: '0.95em' }}>
                                    /demos/images/
                                </span>
                                <input
                                    type="text"
                                    value={imageSuffix}
                                    onChange={e => setImageSuffix(e.target.value)}
                                    style={{ flex: 1, padding: '0.5rem', marginLeft: 4 }}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Add
                        </button>
                    </form>
                    {error && <div style={{ color: 'red', marginTop: '1rem' }}>{error}</div>}
                    {success && <div style={{ color: 'green', marginTop: '1rem' }}>{success}</div>}
                </div>
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default AddDemo;